// REACT IMPORTS
import React, { useState } from "react";
import { Link } from "react-router-dom";

// COMPONENT IMPORTS
import Button from "components/form/Button";
import ToggleSwitch from "components/form/ToggleSwitch";
import Accordion from "components/ui-component/Accordion";

// ICON IMPORTS
// import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { useAppSelector } from "store/hooks";
import logo from "icons/smallLogo.svg";
import { useCookies } from "react-cookie";
import moment from "moment";
import { RootState } from "store/store";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- PREFERENCES ------------------------------------- */

const Preferences = ({ setOpenPreferences }: any) => {
  const tid = useTid();
  const [cookies, setCookie] = useCookies([
    "strictly-necessary",
    "analytics",
    "functional",
    "targeting",
    "acceptCookies",
  ]);
  const [preToggleVal, setPreToggleVal] = useState({
    ac: cookies?.analytics || false,
    fc: cookies?.functional || false,
    tc: cookies?.targeting || false,
  });

  const { paymentLogo } = useAppSelector((state: RootState) => state.payment);

  const handleAllowAll = () => {
    setPreToggleVal({
      ac: true,
      fc: true,
      tc: true,
    });
    setCookie("strictly-necessary", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("analytics", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("functional", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("targeting", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("acceptCookies", "acceptCookies", {
      expires: moment().add(3, "months").toDate(),
    });
    setOpenPreferences(false);
  };

  const handleRejectAll = () => {
    setPreToggleVal({
      ac: false,
      fc: false,
      tc: false,
    });
    setCookie("strictly-necessary", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("analytics", false, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("functional", false, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("targeting", false, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("acceptCookies", "do_not_sell", {
      expires: moment().add(3, "months").toDate(),
    });
    setOpenPreferences(false);
  };

  const onConfirm = () => {
    setCookie("strictly-necessary", true, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("acceptCookies", "do_not_sell", {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("analytics", preToggleVal.ac, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("functional", preToggleVal.fc, {
      expires: moment().add(3, "months").toDate(),
    });
    setCookie("targeting", preToggleVal.tc, {
      expires: moment().add(3, "months").toDate(),
    });
    setOpenPreferences(false);
  };

  return (
    <div
      className="h-full overflow-y-auto lg:pt-[70px] pt-10"
      data-testid={tid("privacy-preferences-panel")}
      aria-label="Privacy preference center"
    >
      <div className="lg:px-10 pr-10 lg:py-5 py-3 flex justify-between ps-4 items-center fixed w-full top-0 bg-bgPrimary z-10">
        <div className="lg:w-fit w-36 flex items-center gap-[10px]">
          {paymentLogo ? (
            <div className="h-16 lg:h-16 flex justify-start">
              <img
                src={paymentLogo}
                alt="Company logo"
                className="animate__animated animate__fadeIn object-contain"
              />
            </div>
          ) : (
            <img
              src={logo}
              alt="DCDial logo"
              className="animate__animated animate__fadeIn"
            />
          )}
          {!paymentLogo && (
            <div className="text-purple font-medium text-xl leading-[30px]">
              CompanyName
            </div>
          )}
        </div>
        <div
          onClick={() => setOpenPreferences(false)}
          onKeyDown={activateOnKey(() => setOpenPreferences(false))}
          className="cursor-pointer"
          role="button"
          tabIndex={0}
          data-testid={tid("privacy-preferences-close-button")}
          aria-label="Close privacy preferences"
        >
          <CloseIcon aria-hidden="true" />
        </div>
      </div>
      <div className="lg:px-10 px-4 pt-4 flex flex-col items-start gap-4">
        <div className="lg:text-lg lg:leading-6 text-base font-bold text-txtPrimary">
          Privacy Preference Center
        </div>
        <p className="lg:text-base text-sm lg:leading-6 font-normal text-txtSecondary text-justify">
          When you visit any web site, it may store or retrieve information on
          your browser, mostly in the form of cookies. This information might be
          about you, your preferences or your device and is mostly used to make
          the site work as you expect it to. The information does not usually
          directly identify you, but it can give you a more personalized web
          experience.
        </p>
        <p className="lg:text-base text-sm lg:leading-6 font-normal text-txtSecondary text-justify">
          Because we respect your right to privacy, you can choose not to allow
          some types of cookies. Click on the different category headings to
          find out more and change our default settings. However, blocking some
          types of cookies may impact your experience of the site and the
          services we are able to offer.
        </p>
        <p className="lg:text-base text-sm lg:leading-6 font-normal text-txtSecondary text-justify">
          In some cases, data obtained from cookies is shared with third parties
          and may be considered a "sale of personal information" under the
          California Consumer Privacy Act (CCPA). You can exercise your right to
          opt-out of that sharing at any time by disabling cookies.{" "}
          <Link
            to="https://www.dcdial.com/legal/cookie-policy"
            target="_blank"
            rel="noreferrer"
            className="cursor-pointer text-btnPrimaryActive underline"
            data-testid={tid("privacy-preferences-cookie-policy-link")}
            aria-label="Read cookie notice"
          >
            Cookie notice
          </Link>
        </p>
        <div className="w-full">
          <Button
            text="Allow All"
            className="w-full"
            onClick={handleAllowAll}
            data-testid={tid("allow-all-cookies-button")}
            aria-label="Allow all cookies"
          />
        </div>
      </div>
      <div className="lg:px-10 px-4 py-10 flex flex-col items-start gap-4">
        <div className="lg:text-lg lg:leading-6 text-base font-bold text-txtPrimary">
          Manage Consent Preferences
        </div>
        <div className="flex flex-col items-start gap-[25px] w-full">
          <div className="flex items-start justify-between pb-[25px] border-b border-strokePrimary w-full">
            <div>
              <Accordion
                title="Strictly Necessary Cookies"
                data-testid={tid("strictly-necessary-cookies-accordion")}
                aria-label="Strictly necessary cookies details"
                content={
                  <div className="text-sm lg:leading-[22px] leading-[20px] text-justify text-txtSecondary pt-[6px] lg:max-w-[400px] max-w-[250px]">
                    These cookies are necessary for the website to function and
                    cannot be switched off in our systems. They are usually only
                    set in response to actions made by you which amount to a
                    request for services, such as setting your privacy
                    preferences, logging in or filling in forms. You can set
                    your browser to block or alert you about these cookies, but
                    some parts of the site will not then work. These cookies do
                    not store any personally identifiable information.
                  </div>
                }
              />
            </div>
            <div className="text-sm font-semibold leading-[22px] text-btnPrimaryActive text-end">
              Always active
            </div>
          </div>
          <div className="flex items-start justify-between pb-[25px] border-b border-strokePrimary w-full">
            <div>
              <Accordion
                title="Analytics Cookies"
                data-testid={tid("analytics-cookies-accordion")}
                aria-label="Analytics cookies details"
                content={
                  <div className="text-sm lg:leading-[22px] leading-[20px] text-justify text-txtSecondary pt-[6px] lg:max-w-[400px] max-w-[250px]">
                    These cookies allow us to count visits and traffic sources,
                    so we can measure and improve the performance of our site.
                    They help us know which pages are the most and least popular
                    and see how visitors move around the site. All information
                    these cookies collect is aggregated and therefore anonymous.
                    If you do not allow these cookies, we will not know when you
                    have visited our site.
                  </div>
                }
              />
            </div>
            <div>
              <ToggleSwitch
                enabled={preToggleVal.ac}
                data-testid={tid("analytics-cookies-toggle")}
                aria-label="Analytics cookies"
                setEnabled={(e: boolean) => {
                  setPreToggleVal({ ...preToggleVal, ac: e });
                }}
              />
            </div>
          </div>
          <div className="flex items-start justify-between pb-[25px] border-b border-strokePrimary w-full">
            <div>
              <Accordion
                title="Functional Cookies"
                data-testid={tid("functional-cookies-accordion")}
                aria-label="Functional cookies details"
                content={
                  <div className="text-sm lg:leading-[22px] leading-[20px] text-justify text-txtSecondary pt-[6px] lg:max-w-[400px] max-w-[250px]">
                    These cookies enable the website to provide enhanced
                    functionality and personalisation. They may be set by us or
                    by third party providers whose services we have added to our
                    pages. If you do not allow these cookies then some or all of
                    these services may not function properly.
                  </div>
                }
              />
            </div>
            <div>
              <ToggleSwitch
                enabled={preToggleVal.fc}
                data-testid={tid("functional-cookies-toggle")}
                aria-label="Functional cookies"
                setEnabled={(e: boolean) => {
                  setPreToggleVal({ ...preToggleVal, fc: e });
                }}
              />
            </div>
          </div>
          <div className="flex items-start justify-between pb-[25px] border-b border-strokePrimary w-full">
            <div>
              <Accordion
                title="Targeting Cookies"
                data-testid={tid("targeting-cookies-accordion")}
                aria-label="Targeting cookies details"
                content={
                  <div className="text-sm lg:leading-[22px] leading-[20px] text-justify text-txtSecondary pt-[6px] lg:max-w-[400px] max-w-[250px]">
                    These cookies may be set through our site by our advertising
                    partners. They may be used by those companies to build a
                    profile of your interests and show you relevant adverts on
                    other sites. They do not store directly personal
                    information, but are based on uniquely identifying your
                    browser and internet device. If you do not allow these
                    cookies, you will experience less targeted advertising.
                  </div>
                }
              />
            </div>
            <div>
              <ToggleSwitch
                enabled={preToggleVal.tc}
                data-testid={tid("targeting-cookies-toggle")}
                aria-label="Targeting cookies"
                setEnabled={(e: boolean) => {
                  setPreToggleVal({ ...preToggleVal, tc: e });
                }}
              />
            </div>
          </div>
        </div>
        <div className="w-full grid lg:grid-cols-2 grid-cols-1 lg:gap-5 gap-3">
          <Button
            text="Reject All"
            onClick={handleRejectAll}
            data-testid={tid("reject-all-cookies-button")}
            aria-label="Reject all optional cookies"
          />
          <Button
            text="Confirm my choices"
            onClick={onConfirm}
            data-testid={tid("confirm-cookie-choices-button")}
            aria-label="Confirm cookie choices"
          />
        </div>
      </div>
    </div>
  );
};

export default Preferences;
